import React, { useState } from 'react';
import { Account } from '../../../models';
import {
  Button,
  Highlight,
  IconBannersWarning,
  HStack,
  Text,
  VStack,
  IconCheckCircle,
} from '@adc/parallax-component-library';
import mediator from '../../../utilities/mediator';
import i18n from '../../../utilities/i18n';
import { UserStatus as AccountStatus } from 'adc-sirius-module-common';
import { Dialog } from 'tamagui';
import { useAppSelector } from '../../../reducers/hooks';

type Action =
  | 'sendPasswordReset'
  | 'resendInvite'
  | 'suspend'
  | 'unsuspend'
  | 'sendInvite';

type AccountActionButtonGroupProps = {
  account: Account;
  onSuccess: () => void;
};

type ActionButtonWithModalProps = {
  action: Action;
} & AccountActionButtonGroupProps;

const ActionButtonWithModal = ({
  action,
  account,
  onSuccess,
}: ActionButtonWithModalProps) => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const topic = `account:${action === 'sendInvite' ? 'resendInvite' : action}`;

  const button = (
    <Button
      size="small"
      variant="tertiary"
      label={i18n.t(`Page_Accounts.action.${action}.label`)}
      onPress={() => setOpen(true)}
      singleLine
      testID={action}
    />
  );

  return (
    <Dialog open={open}>
      <Dialog.Trigger asChild>{button}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay key="overlay" />
        <Dialog.Content key="content">
          <Dialog.Title>
            <Text type="$h3.strong">
              {i18n.t(`Page_Accounts.action.${action}.label`)}
            </Text>
          </Dialog.Title>
          <VStack gap="$2" paddingVertical="$4">
            {error ? (
              <Highlight
                variant="danger"
                iconStart={
                  <IconBannersWarning
                    color="$neutral.0"
                    accentColor="$support.danger.default"
                  />
                }
                title={i18n.t('Common.errorGenericTitle')}
                body={i18n.t('Common.errorGeneric')}
                testID="error-banner"
              />
            ) : null}

            {success ? (
              <Highlight
                variant="success"
                title={i18n.t('Common.success')}
                body={i18n.t(`Page_Accounts.action.${action}.success`, {
                  email: account.email,
                })}
                iconStart={
                  <IconCheckCircle
                    color="$neutral.0"
                    accentColor="$support.positive.default"
                  />
                }
                testID="success-banner"
              />
            ) : (
              <Text>
                {i18n.t(`Page_Accounts.action.${action}.prompt`, {
                  email: account.email,
                })}
              </Text>
            )}
          </VStack>

          <HStack justifyContent="flex-end" gap="$2">
            {success ? (
              <Button
                variant="secondary"
                label={i18n.t('Common.close')}
                aria-label={i18n.t('Common.close')}
                testID="close"
                onPress={() => {
                  setError(false);
                  setOpen(false);
                  setSuccess(false);
                }}
              />
            ) : (
              <>
                <Button
                  variant="secondary"
                  label={i18n.t('Common.cancel')}
                  aria-label={i18n.t('Common.cancel')}
                  testID="cancel"
                  onPress={() => {
                    setError(false);
                    setOpen(false);
                  }}
                />
                <Button
                  variant="primary"
                  onPress={() => {
                    setLoading(true);
                    const request =
                      action === 'resendInvite' ||
                      action === 'sendPasswordReset' ||
                      action === 'sendInvite'
                        ? { data: account.email }
                        : { key: account.key };

                    mediator
                      .request<void>(topic, request)
                      .then(() => {
                        onSuccess();
                        setSuccess(true);
                      })
                      .catch(() => setError(true))
                      .finally(() => setLoading(false));
                  }}
                  label={i18n.t('Common.ok')}
                  testID="confirm"
                  disabled={loading}
                />
              </>
            )}
          </HStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

export const AccountActionButtonGroup = ({
  account,
  onSuccess,
}: AccountActionButtonGroupProps) => {
  const capabilities = useAppSelector((state) => state.capabilities);

  const actions: Action[] = [];

  if (account.status !== AccountStatus.Unknown) {
    if (
      account.status !== AccountStatus.Locked &&
      account.status !== AccountStatus.Suspended &&
      account.status !== AccountStatus.NotInvited
    ) {
      actions.push('sendPasswordReset');
    }

    if (
      account.status !== AccountStatus.Verified &&
      account.status !== AccountStatus.Locked &&
      account.status !== AccountStatus.Suspended &&
      account.status !== AccountStatus.NotInvited
    ) {
      actions.push('resendInvite');
    }

    if (
      capabilities?.canUpdateUsers &&
      account.status === AccountStatus.Verified
    ) {
      actions.push('suspend');
    }

    if (
      capabilities?.canUpdateUsers &&
      account.status === AccountStatus.Suspended
    ) {
      actions.push('unsuspend');
    }

    if (account.status === AccountStatus.NotInvited) {
      actions.push('sendInvite');
    }
  }

  return (
    <HStack gap="$1" testID={`${account.key}-action-button-group`}>
      {actions.map((action) => (
        <ActionButtonWithModal
          key={`${account.key}-${action}`}
          action={action}
          onSuccess={onSuccess}
          account={account}
        />
      ))}
    </HStack>
  );
};
